import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { getSentProfileViewRequestsAction } from "../../redux/actions/profileViewRequestActions";

const ProfileHeader = ({ 
  user, 
  activeTab, 
  setActiveTab, 
  isPrivateProfile = false,
  accessDenied = false,
  profileUserId = null,
  onRequestAccess = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false
}) => {
  const dispatch = useDispatch();

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId]);
  const tabs = ["Posts", "Skills", "About", "Employment", "Learnings", "Others"];

  const maskEmail = (email) => {
    if (!email || typeof email !== "string") return "xxx@xxx.xxx";
    const [local, domain] = email.split("@");
    if (!domain || !local) return "xxx@xxx.xxx";
    
    // Show first 4 characters of local part, then "x" repeated for remaining chars, then full domain
    // Example: naha@gmail.com (4 chars) -> naha@gmail.com (no masking needed if short)
    // Example: nahas@gmail.com (5 chars) -> nahax@gmail.com
    // Example: nahasree@gmail.com (8 chars) -> nahaxxxx@gmail.com
    const showChars = Math.min(4, Math.floor(local.length / 2)); // Show up to 4 chars or half, whichever is smaller
    const hiddenChars = local.length - showChars;
    
    if (hiddenChars <= 0) {
      // If local part is very short, show it all
      return `${local}@${domain}`;
    }
    
    const visiblePart = local.substring(0, showChars);
    const maskedPart = "x".repeat(hiddenChars);
    return `${visiblePart}${maskedPart}@${domain}`;
  };

  const maskMobile = (mobile) => {
    if (!mobile) return "xxx-xxx-xxxx";
    // Remove any non-digit characters for processing
    const digitsOnly = mobile.toString().replace(/\D/g, "");
    if (!digitsOnly || digitsOnly.length === 0) return "xxx-xxx-xxxx";
    
    // Show first 3 digits, then "x" repeated for middle digits, then last 2 digits
    // Example: 9876543210 (10 digits) -> 987xxxxx210
    // Example: 98765432 (8 digits) -> 987xxx32
    // Example: 98765 (5 digits) -> 98xxx5
    if (digitsOnly.length <= 3) {
      // If too short, show first 1 + x's for rest
      const first = digitsOnly.substring(0, 1);
      const hidden = digitsOnly.length - 1;
      return `${first}${"x".repeat(hidden)}`;
    } else if (digitsOnly.length <= 5) {
      // If short, show first 2 + x's + last 1
      const first = digitsOnly.substring(0, 2);
      const last = digitsOnly.substring(digitsOnly.length - 1);
      const hidden = digitsOnly.length - 2 - 1;
      return `${first}${"x".repeat(hidden)}${last}`;
    } else {
      // Normal case: first 3 + x's + last 2
      const first = digitsOnly.substring(0, 3);
      const last = digitsOnly.substring(digitsOnly.length - 2);
      const hidden = digitsOnly.length - 3 - 2;
      return `${first}${"x".repeat(hidden)}${last}`;
    }
  };

  const maskValue = (value) => {
    if (!value) return "xxx";
    // Show first 3 characters, then "x" repeated for remaining chars
    const str = value.toString();
    const showChars = Math.min(3, Math.floor(str.length / 2)); // Show up to 3 chars or half, whichever is smaller
    const hiddenChars = str.length - showChars;
    
    if (hiddenChars <= 0) {
      // If value is very short, show it all
      return str;
    }
    
    const visiblePart = str.substring(0, showChars);
    const maskedPart = "x".repeat(hiddenChars);
    return `${visiblePart}${maskedPart}`;
  };

  const displayEmail = isPrivateProfile ? maskEmail(user.email) : user.email;
  const displayMobile = isPrivateProfile ? maskMobile(user.mobileNumber) : user.mobileNumber;
  const displayCountry = isPrivateProfile ? maskValue(user.country) : user.country;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-2 md:p-1 mb-3">
      <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-2 mb-1">
        <div className="relative ml-2 mt-2 mb-2">
        <img
            className="h-20 w-20 md:h-22 md:w-22 rounded-full object-cover border-4 border-gray-300 ring-2 ring-gray-100 ring-offset-2 ring-offset-white shadow-md"
            src={user.avatar}
            alt="Profile"
          />
        </div>
        
        <div className="flex-1 -mt-4">
          <div className="mb-2">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 font-heading">
              {user.name}
            </h1>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-3 text-xs md:text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="font-roboto-flex text-gray-500">
                {displayEmail || "••••••••"}
              </span>
            </div>
            {user.mobileNumber && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-roboto-flex text-gray-500">
                  {displayMobile}
                </span>
              </div>
            )}
            {user.country && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-roboto-flex text-gray-500">
                  {displayCountry}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="pt-0 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row w-full pt-0">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-2 sm:px-3 md:px-4 font-medium text-xs sm:text-sm md:text-base transition-all duration-200 font-heading flex items-center gap-1 sm:gap-2 justify-center border-b-2 flex-1 ${
                activeTab === tab
                  ? "text-primary border-primary"
                  : "text-gray-600 hover:text-gray-800 hover:text-primary border-transparent hover:border-gray-300"
              }`}
            >
              {tab === "Posts" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              {tab === "About" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              )}
              {tab === "Skills" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
              {tab === "Employment" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              )}
              {tab === "Learnings" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              )}
              {tab === "Others" && (
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h-01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                </svg>
              )}
              <span className="whitespace-nowrap">{tab}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
